import { Button, Flex, HStack, Icon, Stack, Text, useToast } from '@chakra-ui/react';
import { LoadingSpinner } from 'components/LoadingSpinner';
import { downloadFile } from 'helpers/downloadHelper';
import { dropzone } from 'helpers/makeFormFields';
import useFileUploadMutation from 'hooks/mutations/useFileUploadMutation';
import useUploadWorkshopHandwerkskarteMutation from 'hooks/mutations/useUploadWorkshopHandwerkskarteMutation';
import useFileQueryV2 from 'hooks/useFileQueryV2';
import { ContentCard } from 'layout/ContentCard';
import { useEffect, useState } from 'react';
import { IFileWithMeta } from 'react-dropzone-uploader';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AiOutlineFileText } from 'react-icons/ai';
import { IoTrash } from 'react-icons/io5';

import { FileData, Workshop, WorkshopVerificationRequest } from '../../api/types';
import { fontWeights } from '../../global/Fonts';

const HANDWERKSKARTE_COUNTRY = 'DE';

type PickedFile = {
  id: string;
  name: string;
  file: File;
};

const WorkshopHandwerkskarteCard = ({ workshopId, workshop }: { workshopId: number; workshop: Workshop | undefined }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [isReplacing, setIsReplacing] = useState(false);
  const [pickedFiles, setPickedFiles] = useState<PickedFile[]>([]);
  const [dropzoneKey, setDropzoneKey] = useState(0);

  const {
    control,
    formState: { errors },
  } = useForm();

  const fileUploadMutation = useFileUploadMutation();
  const uploadHandwerkskarteMutation = useUploadWorkshopHandwerkskarteMutation(workshopId);

  const isGermanWorkshop = workshop?.country?.toUpperCase() === HANDWERKSKARTE_COUNTRY;
  const verification = workshop?.verifications?.find((v: WorkshopVerificationRequest) => v.status !== 900);
  const handwerkskarte = verification?.handwerkskarte;

  const [downloadUrl, setDownloadUrl] = useState<string | undefined>(undefined);
  const [downloadDocument, setDownloadDocument] = useState<FileData | undefined>(undefined);
  const [downloadClick, setDownloadClick] = useState(false);
  const fileQuery = useFileQueryV2(downloadUrl);

  useEffect(() => {
    downloadFile(downloadDocument, fileQuery.data);
    setDownloadClick(false);
  }, [fileQuery.data, downloadClick, downloadDocument]);

  const onDownload = () => {
    if (!handwerkskarte) return;

    setDownloadUrl(`workshop-verification-request/file/${handwerkskarte.id}/download`);
    setDownloadDocument(handwerkskarte);
    setDownloadClick(true);
  };

  if (!isGermanWorkshop) {
    return null;
  }

  const isEditing = isReplacing || handwerkskarte == null;
  const isPending = fileUploadMutation.isPending || uploadHandwerkskarteMutation.isPending;

  const onFilePicked = (fileWithMeta: IFileWithMeta | undefined) => {
    if (!fileWithMeta) return;

    setPickedFiles((files) => [...files, { id: fileWithMeta.meta.id, name: fileWithMeta.meta.name, file: fileWithMeta.file }]);
    setDropzoneKey((key) => key + 1);
  };

  const removePickedFile = (id: string) => setPickedFiles((files) => files.filter((file) => file.id !== id));

  const resetSelection = () => {
    setPickedFiles([]);
    setDropzoneKey((key) => key + 1);
  };

  const onSubmit = async () => {
    if (!pickedFiles.length) return;

    const uuids: string[] = [];
    for (const picked of pickedFiles) {
      const formData = new FormData();
      formData.append('file', picked.file);
      const response = await fileUploadMutation.mutateAsync(formData).catch(() => null);
      if (!response) return;
      uuids.push(response.data.uuid);
    }

    uploadHandwerkskarteMutation.mutate(
      { handwerkskarte_uuids: uuids },
      {
        onSuccess: () => {
          resetSelection();
          setIsReplacing(false);
          toast({ title: t('workshop_verification_request:handwerkskarte.upload_success'), status: 'success' });
        },
        onError: (error: any) => {
          toast({ title: error?.response?.data?.message ?? error?.message, status: 'error' });
        },
      },
    );
  };

  return (
    <ContentCard header={t('workshop_verification_request:handwerkskarte.label')} data-test-id="workshop-handwerkskarte-card">
      <Flex direction="column" w="100%" gap={4}>
        {!isEditing && (
          <HStack spacing={2} color="blue.500" data-test-id="workshop-handwerkskarte-file-name">
            <Icon as={AiOutlineFileText} boxSize="5" />
            <Text as="span" fontSize="md" fontWeight={fontWeights.bold}>
              {handwerkskarte?.original_name ?? handwerkskarte?.name}
            </Text>
          </HStack>
        )}

        {isEditing &&
          (isPending ? (
            <LoadingSpinner noMargin />
          ) : (
            <Flex key={dropzoneKey} direction="column" w="100%">
              {dropzone({
                name: 'handwerkskarte',
                control,
                errors,
                onChange: onFilePicked,
              } as Parameters<typeof dropzone>[0])}
            </Flex>
          ))}

        {isEditing && pickedFiles.length > 0 && (
          <Stack spacing={2} data-test-id="workshop-handwerkskarte-picked-files">
            {pickedFiles.map((picked) => (
              <HStack key={picked.id} justifyContent="space-between" spacing={4}>
                <HStack spacing={2} color="blue.500">
                  <Icon as={AiOutlineFileText} boxSize="5" />
                  <Text as="span" fontSize="md" fontWeight={fontWeights.bold}>
                    {picked.name}
                  </Text>
                </HStack>
                <Button
                  size="sm"
                  variant="link"
                  colorScheme="red"
                  onClick={() => removePickedFile(picked.id)}
                  leftIcon={<Icon as={IoTrash} boxSize="5" />}
                  disabled={isPending}
                >
                  {t('common:remove')}
                </Button>
              </HStack>
            ))}
          </Stack>
        )}

        <Flex justifyContent="flex-end" gap={2}>
          {isReplacing && (
            <Button
              variant="outline"
              onClick={() => {
                resetSelection();
                setIsReplacing(false);
              }}
              disabled={isPending}
            >
              {t('common:cancel')}
            </Button>
          )}

          {isEditing ? (
            <Button
              variant="primary"
              onClick={onSubmit}
              isLoading={isPending}
              disabled={!pickedFiles.length || isPending}
              data-test-id="workshop-handwerkskarte-save"
            >
              {t('common:save')}
            </Button>
          ) : (
            <>
              <Button variant="primary" onClick={() => setIsReplacing(true)} data-test-id="workshop-handwerkskarte-replace">
                {t('workshop_verification_request:replace')}
              </Button>
              <Button
                variant="primary"
                colorScheme="blue"
                onClick={onDownload}
                isLoading={fileQuery.isFetching}
                data-test-id="workshop-handwerkskarte-download"
              >
                {t('common:download')}
              </Button>
            </>
          )}
        </Flex>
      </Flex>
    </ContentCard>
  );
};

export default WorkshopHandwerkskarteCard;
